if (!customElements.get('expandable-content')) {
  customElements.define(
    'expandable-content',
    class ExpandableContent extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.expandableItems = this.querySelectorAll('.expandable-item');
        this.showMoreText = this.dataset.showMoreText || 'Show more';
        this.showLessText = this.dataset.showLessText || 'Show less';

        this.expandableItems.forEach((item) => {
          const toggle = item.querySelector('.expandable-item__toggle');
          const content = item.querySelector('.expandable-item__content');
          const fade = item.querySelector('.expandable-item__fade');

          if (toggle && content) {
            // Check if content needs expansion (content is taller than clamped height)
            this.checkContentOverflow(item, content, toggle, fade);

            toggle.addEventListener('click', (e) => {
              e.preventDefault();
              this.toggleExpand(item, toggle, content, fade);
            });
          }
        });

        // Re-check on window resize
        window.addEventListener('resize', this.debounce(() => {
          this.expandableItems.forEach((item) => {
            const toggle = item.querySelector('.expandable-item__toggle');
            const content = item.querySelector('.expandable-item__content');
            const fade = item.querySelector('.expandable-item__fade');

            if (toggle && content && !content.classList.contains('is-expanded')) {
              this.checkContentOverflow(item, content, toggle, fade);
            }
          });
        }, 250));
      }

      checkContentOverflow(item, content, toggle, fade) {
        // Temporarily remove clamping to measure full height
        const wasClamped = content.classList.contains('expandable-item__content--clamped');
        content.classList.remove('expandable-item__content--clamped');
        const fullHeight = content.scrollHeight;
        
        if (wasClamped) {
          content.classList.add('expandable-item__content--clamped');
        }
        
        const clampedHeight = content.clientHeight;

        // If content fits within clamped area, hide the toggle
        if (fullHeight <= clampedHeight + 5) { // 5px tolerance
          toggle.style.display = 'none';
          if (fade) fade.classList.add('hidden');
          content.classList.remove('expandable-item__content--clamped');
        } else {
          toggle.style.display = '';
          if (fade && !content.classList.contains('is-expanded')) {
            fade.classList.remove('hidden');
          }
        }
      }

      toggleExpand(item, toggle, content, fade) {
        const isExpanded = content.classList.contains('is-expanded');
        const toggleText = toggle.querySelector('.expandable-item__toggle-text');

        if (isExpanded) {
          // Collapse
          content.classList.remove('is-expanded');
          content.classList.add('expandable-item__content--clamped');
          toggle.setAttribute('aria-expanded', 'false');
          if (toggleText) toggleText.textContent = this.showMoreText;
          if (fade) fade.classList.remove('hidden');

          // Animate collapse
          this.animateCollapse(content);
        } else {
          // Expand - capture heights BEFORE changing classes
          const startHeight = content.clientHeight;
          
          // Lock the height before removing clamp
          content.style.height = `${startHeight}px`;
          content.style.overflow = 'hidden';
          
          // Now remove clamp and add expanded class
          content.classList.remove('expandable-item__content--clamped');
          content.classList.add('is-expanded');
          toggle.setAttribute('aria-expanded', 'true');
          if (toggleText) toggleText.textContent = this.showLessText;
          if (fade) fade.classList.add('hidden');

          // Animate expand
          this.animateExpand(content, startHeight);
        }
      }

      async animateExpand(content, startHeight) {
        // Force reflow
        content.offsetHeight;

        const endHeight = content.scrollHeight;

        if (typeof FoxTheme !== 'undefined' && FoxTheme.Motion) {
          await FoxTheme.Motion.animate(
            content,
            { height: [`${startHeight}px`, `${endHeight}px`] },
            { duration: 0.35, easing: 'ease' }
          ).finished;
        } else {
          content.style.transition = 'height 0.35s ease';
          content.style.height = `${endHeight}px`;
          await new Promise(resolve => setTimeout(resolve, 350));
        }

        content.style.height = '';
        content.style.overflow = '';
        content.style.transition = '';
      }

      async animateCollapse(content) {
        const startHeight = content.scrollHeight;
        content.style.height = `${startHeight}px`;
        content.style.overflow = 'hidden';

        // Force reflow
        content.offsetHeight;

        // Temporarily remove clamp to get proper collapsed height
        content.classList.remove('expandable-item__content--clamped');
        content.classList.add('expandable-item__content--clamped');
        
        // We need to calculate the clamped height
        const tempDiv = content.cloneNode(true);
        tempDiv.style.position = 'absolute';
        tempDiv.style.visibility = 'hidden';
        tempDiv.style.height = 'auto';
        tempDiv.classList.add('expandable-item__content--clamped');
        tempDiv.classList.remove('is-expanded');
        content.parentNode.appendChild(tempDiv);
        const endHeight = tempDiv.clientHeight;
        tempDiv.remove();

        if (typeof FoxTheme !== 'undefined' && FoxTheme.Motion) {
          await FoxTheme.Motion.animate(
            content,
            { height: [`${startHeight}px`, `${endHeight}px`] },
            { duration: 0.35, easing: 'ease' }
          ).finished;
        } else {
          content.style.transition = 'height 0.35s ease';
          content.style.height = `${endHeight}px`;
          await new Promise(resolve => setTimeout(resolve, 350));
        }

        content.style.height = '';
        content.style.overflow = '';
        content.style.transition = '';
      }

      debounce(func, wait) {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => func.apply(this, args), wait);
        };
      }
    }
  );
}
